import React, { useMemo, useState } from 'react';
import { gql, useApolloClient, useMutation } from '@apollo/client';
import { toast } from '@/components/ui/toast';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Loader2, Mail } from 'lucide-react';

const LIST_USERS_BY_EMAIL = gql`
  query ListUsersByEmail($filters: FilterOptions!) {
    listUsers(filters: $filters) {
      entries {
        id
        username
        name
        email
        disabled
        twoFactorStatus
      }
    }
  }
`;

const SEND_TWO_FACTOR_ENROLLMENT_NOTIFICATION = gql`
  mutation SendTwoFactorEnrollmentNotification($input: TwoFactorEnrollmentNotificationInput!) {
    sendTwoFactorEnrollmentNotification(input: $input) {
      success
      enrollmentUrl
      status
    }
  }
`;

type ListUsersResponse = {
  listUsers?: {
    entries?: {
      id: string;
      username: string;
      name?: string | null;
      email?: string | null;
      disabled?: boolean | null;
      twoFactorStatus?: string | null;
    }[];
  };
};

type ValidationStatus = 'pending' | 'found' | 'not_found' | 'error';
type SendStatus = 'idle' | 'sending' | 'sent' | 'failed';

type EnrollmentRow = {
  key: string;
  inputEmail: string;
  normalizedEmail: string;
  userId?: number;
  username?: string;
  name?: string;
  email?: string;
  selected: boolean;
  validationStatus: ValidationStatus;
  validationMessage?: string;
  sendStatus: SendStatus;
  sendMessage?: string;
};

const validationStyles: Record<ValidationStatus, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive'; className?: string }> = {
  pending: { label: 'Pending', variant: 'secondary' },
  found: { label: 'Found', variant: 'default', className: 'bg-green-100 text-green-800' },
  not_found: { label: 'Not found', variant: 'destructive' },
  error: { label: 'Error', variant: 'destructive' }
};

const sendStyles: Record<SendStatus, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive'; className?: string }> = {
  idle: { label: 'Idle', variant: 'outline' },
  sending: { label: 'Sending', variant: 'secondary' },
  sent: { label: 'Sent', variant: 'default', className: 'bg-green-100 text-green-800' },
  failed: { label: 'Failed', variant: 'destructive' }
};

const parseEmailList = (value: string) => {
  if (!value) return [];

  return value
    .split(/[\n,]+/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0)
    .map((entry) => entry.replace(/^["']|["']$/g, ''));
};

export const BulkEnrollmentEmailsPage: React.FC = () => {
  const client = useApolloClient();
  const [rawEmails, setRawEmails] = useState('');
  const [rows, setRows] = useState<EnrollmentRow[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [currentEmail, setCurrentEmail] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentTotal, setCurrentTotal] = useState(0);

  const [sendEnrollmentEmail] = useMutation(SEND_TWO_FACTOR_ENROLLMENT_NOTIFICATION);

  const selectedCount = useMemo(() => rows.filter((row) => row.selected).length, [rows]);
  const totalCount = rows.length;
  const progressRows = useMemo(() => rows.filter((row) => row.selected), [rows]);
  const progressSummary = useMemo(() => {
    let sent = 0;
    let failed = 0;
    let sending = 0;
    let idle = 0;

    progressRows.forEach((row) => {
      if (row.sendStatus === 'sent') sent += 1;
      if (row.sendStatus === 'failed') failed += 1;
      if (row.sendStatus === 'sending') sending += 1;
      if (row.sendStatus === 'idle') idle += 1;
    });

    const total = progressRows.length;
    const remaining = Math.max(total - sent - failed, 0);
    return { total, sent, failed, sending, idle, remaining };
  }, [progressRows]);

  const updateRow = (key: string, updates: Partial<EnrollmentRow>) => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...updates } : row)));
  };

  const handleInputChange = (value: string) => {
    setRawEmails(value);
    setRows([]);
  };

  const handleValidate = async () => {
    if (isValidating) return;

    const emails = parseEmailList(rawEmails);
    if (emails.length === 0) {
      toast.error('Enter at least one email address');
      return;
    }

    const pendingRows = emails.map((inputEmail, index) => {
      const normalizedEmail = inputEmail.toLowerCase();
      return {
        key: `${index}-${normalizedEmail}`,
        inputEmail,
        normalizedEmail,
        selected: true,
        validationStatus: 'pending' as ValidationStatus,
        sendStatus: 'idle' as SendStatus
      };
    });

    setRows(pendingRows);
    setIsValidating(true);

    for (let index = 0; index < pendingRows.length; index += 1) {
      const row = pendingRows[index];

      try {
        const { data } = await client.query<ListUsersResponse>({
          query: LIST_USERS_BY_EMAIL,
          variables: {
            filters: {
              email: row.normalizedEmail,
              limit: 25,
              offset: 0,
              showDisabled: true,
              showLocked: true
            }
          },
          fetchPolicy: 'network-only'
        });

        const entries = data?.listUsers?.entries || [];
        const matches = entries.filter((entry) => (entry.email || '').toLowerCase() === row.normalizedEmail);
        const updates: Partial<EnrollmentRow> = {};

        if (matches.length === 0) {
          updates.validationStatus = 'not_found';
        }

        if (matches.length > 0) {
          const match = matches[0];
          const userId = Number(match.id);
          if (!Number.isFinite(userId)) {
            updates.validationStatus = 'error';
            updates.validationMessage = 'Invalid user id';
          }

          if (Number.isFinite(userId)) {
            updates.validationStatus = 'found';
            updates.userId = userId;
            updates.username = match.username || '';
            updates.name = match.name || '';
            updates.email = match.email || row.inputEmail;
          }
        }

        if (matches.length > 1) {
          updates.validationMessage = `Multiple matches (${matches.length})`;
        }

        updateRow(row.key, updates);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Validation failed';
        updateRow(row.key, {
          validationStatus: 'error',
          validationMessage: message
        });
      }
    }
    setIsValidating(false);
  };

  const handleToggleRow = (key: string, checked: boolean) => {
    updateRow(key, { selected: checked });
  };

  const handleSend = async () => {
    if (isSending) return;

    const selectedRows = rows.filter((row) => row.selected);
    if (selectedRows.length === 0) {
      toast.error('Select at least one row to send');
      return;
    }

    setCurrentTotal(selectedRows.length);
    setCurrentIndex(0);
    setCurrentEmail('');
    setIsSending(true);

    for (let index = 0; index < selectedRows.length; index += 1) {
      const row = selectedRows[index];
      setCurrentIndex(index + 1);
      setCurrentEmail(row.inputEmail);
      if (!row.userId || !Number.isFinite(row.userId)) {
        updateRow(row.key, {
          sendStatus: 'failed',
          sendMessage: 'Missing user id'
        });
        continue;
      }

      updateRow(row.key, { sendStatus: 'sending', sendMessage: undefined });
      try {
        const { data } = await sendEnrollmentEmail({
          variables: {
            input: { userId: row.userId }
          }
        });

        if (data?.sendTwoFactorEnrollmentNotification?.success) {
          updateRow(row.key, { sendStatus: 'sent' });
          continue;
        }

        updateRow(row.key, {
          sendStatus: 'failed',
          sendMessage: data?.sendTwoFactorEnrollmentNotification?.status || 'Send failed'
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Send failed';
        updateRow(row.key, { sendStatus: 'failed', sendMessage: message });
      }
    }

    setIsSending(false);
    setCurrentEmail('');
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-3">
        <Mail className="h-7 w-7 text-blue-600" />
        <div>
          <h1 className="text-2xl font-semibold">Bulk Enrollment Emails</h1>
          <p className="text-sm text-muted-foreground">
            Paste email addresses, validate them, then send enrollment emails sequentially.
          </p>
        </div>
      </div>

      {rows.length === 0 && (
        <Card className="p-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bulk-enrollment-input">Email list</Label>
            <Textarea
              id="bulk-enrollment-input"
              placeholder="one@example.com, two@example.com"
              value={rawEmails}
              onChange={(event) => handleInputChange(event.target.value)}
              rows={6}
            />
            <p className="text-xs text-muted-foreground">One per line or comma separated.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={handleValidate} disabled={isValidating || rawEmails.trim().length === 0}>
              {isValidating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Validate
            </Button>
            <div className="text-sm text-muted-foreground">
              {totalCount > 0 ? `${selectedCount} of ${totalCount} selected` : 'No validated results yet'}
            </div>
          </div>
        </Card>
      )}

      {rows.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <div className="flex items-center justify-between gap-3 p-4 border-b">
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">
                {selectedCount} selected
              </div>
              {isSending && (
                <div className="text-xs text-muted-foreground">
                  Sending {currentIndex} of {currentTotal}
                  {currentEmail ? `: ${currentEmail}` : ''}
                </div>
              )}
              {progressSummary.total > 0 && (
                <div className="flex flex-wrap gap-2 text-xs">
                  <Badge variant="secondary">Total {progressSummary.total}</Badge>
                  <Badge variant="default" className="bg-green-100 text-green-800">Sent {progressSummary.sent}</Badge>
                  <Badge variant="destructive">Failed {progressSummary.failed}</Badge>
                  <Badge variant="outline">Remaining {progressSummary.remaining}</Badge>
                </div>
              )}
            </div>
            <Button onClick={handleSend} disabled={isSending || selectedCount === 0}>
              {isSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send Enrollment Emails
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">Select</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Validation</TableHead>
                <TableHead>Send</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const validationStyle = validationStyles[row.validationStatus];
                const sendStyle = sendStyles[row.sendStatus];
                return (
                  <TableRow key={row.key}>
                    <TableCell>
                      <Checkbox
                        checked={row.selected}
                        onCheckedChange={(checked) => handleToggleRow(row.key, Boolean(checked))}
                        disabled={isSending}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs">{row.inputEmail}</TableCell>
                    <TableCell>
                      <div className="text-sm">{row.username || '-'}</div>
                      <div className="text-xs text-muted-foreground">
                        {row.userId ? `ID ${row.userId}` : 'No user id'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={validationStyle.variant} className={validationStyle.className}>
                        {validationStyle.label}
                      </Badge>
                      {row.validationMessage && (
                        <div className="text-xs text-muted-foreground mt-1">{row.validationMessage}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={sendStyle.variant} className={sendStyle.className}>
                        {sendStyle.label}
                      </Badge>
                      {row.sendMessage && (
                        <div className="text-xs text-muted-foreground mt-1">{row.sendMessage}</div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};
